import {
	Avatar,
	Box,
	CircularProgress,
	Collapse,
	IconButton,
	Pagination,
	Typography,
} from '@mui/material';
import CustomButton from '@src/components/CustomButton/CustomButton';
import CustomDataGrid2 from '@src/components/CustomDataGrid/CustomDataGrid2';
import CustomDrawer from '@src/components/CustomDrawer/CustomDrawer';
import { DeleteIcon, LockIcon, PermissionIcon } from '@src/components/Icons';
import { IsMobileWidth, IsTabletWidth } from '@src/components/MediaQueryUtils';
import SearchBar from '@src/components/SearchBar';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import UserCreation from './Components/Forms/UserCreation';
import UserEditable from './Components/Forms/UserEditable';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import CustomModal from '@src/components/CustomModal';
import ConfirmModal from '@src/components/ConfirmModal/ConfirmModal';
import { AxiosError, AxiosResponse } from 'axios';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { getBaseRoute, truncateField } from '@src/helpers/Helpers';
import { setAllUsers, setPageNumber } from '@src/store/reducers/teamReducer';
import { CustomAlertType } from '@src/interfaces/type';
import Permission from './Permission';
import { adminUsers } from '@src/helpers/Permission';
import CustomPermission from '@src/components/Permissions/CustomPermission';
import { clearUserSession } from '@src/helpers/ApiHelper';
import { Check, Clear } from '@mui/icons-material';
import Loading from '@src/components/Loading/Loading';
import { SelectedItemsTextContainer } from '@src/components/CommonComponents/Common';
import CustomTextButton from '@src/components/CustomButton/CustomTextButton';
import SettingsCard from '@src/components/CustomCard/SettingsCard';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	max-width: 100%;
	height: 100%;
	background-color: var(--chat-header-bg);
	overflow: scroll;
	padding: 24px;
	gap: 24px;
	.container1 {
		display: flex;
		justify-content: space-between;
		align-items: center;
		p {
			color: var(--color-font-grey);
			font-size: 14px;
			font-weight: 600;
		}
	}
	@media screen and (max-width: 600px) {
		margin-bottom: 68px;
	}
`;
const SelectedItemsContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 5px;
	font-size: 16px;
	font-weight: 750;
	line-height: 21.82px;
	transition: width 0.3s ease;
`;
interface MediaQueries {
	'@media (max-width: 600px)': React.CSSProperties;
}
const stylingDeleteModal: React.CSSProperties & MediaQueries = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: '306px',
	height: '375px',
	backgroundColor: 'background.paper',
	borderRadius: '4px',
	boxShadow: '24px',
	'@media (max-width: 600px)': {
		width: '306px',
	},
};

//For Mobile View
const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 72px;
	padding: 16px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 10;
	flex-shrink: 0; // Prevents resizing when the content overflows
	position: absolute;
	bottom: 0px;
`;

const StyledMobileContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
	overflow: hidden;
	width: 100%;
	#order-details {
		display: flex;
		flex-direction: column;
		gap: 10px;
		padding: 16px;
		flex-shrink: 0;
		.field {
			display: flex;
			gap: 6px;
			color: #5c5c5c;
			font-size: 14px;
			font-weight: 500;
			span {
				font-weight: 700;
			}
		}
	}
	#filter-container {
		display: flex;
		gap: 10px;
		padding: 16px;
	}
`;

const Container = styled.div({
	background: '#F1F1F1',
	paddingTop: '2px',
	overflowY: 'auto',
	flexGrow: 1, // Ensure it takes up remaining space and allows scrolling
	marginBottom: '235px',
});
type TimeoutHandler = ReturnType<typeof setTimeout> | null;
interface Props {
	displayCustomError: any;
	setAlert: (data: CustomAlertType) => void;
	view: any;
	setView: any;
	setSearchValue: any;
	searchValue: any;
}

const TeamsAndPermission: React.FC<Props> = ({
	setAlert,
	displayCustomError,
	view,
	setView,
	setSearchValue,
	searchValue,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const allUsers: any = useAppSelector((state) => state?.team?.allUsers);
	const pageNumber: any = useAppSelector((state) => state?.team?.pageNumber);

	const navigate = useNavigate();
	const { id } = useParams();
	const dispatch = useAppDispatch();

	const [deleteProductModal, setDeleteProductModal] = useState(false);
	const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
	const [openDrawer, setOpenDrawer] = useState(false);
	const [openEditDrawer, setOpenEditDrawer] = useState(false);
	const [loadingData, setLoadingData] = useState(true);
	const [loading, setLoading] = useState(false);
	const [searching, setSearching] = useState(false);
	const [isApiCall, setApiCall] = useState(false);
	const [operation, setOperation] = useState('');
	const [rows, setRows] = useState([]);
	const [rowData, setRowData] = useState([]);
	const timeoutHandler = useRef<TimeoutHandler>(null);
	const [collapse, setCollapse] = useState(false);
	const [loadingItems, setLoadingItems] = useState(false);
	const [totalCount, setTotalCount] = useState(0);
	const isAdmin = adminUsers?.includes(currentUser?.userType);

	const isMobileWidth = IsMobileWidth();

	const handleRecover = (row: any) => {
		const data = {
			name: row?.firstname,
			phone: row?.id,
			email: row?.email,
			image: row?.image || null,
			type: row?.type,
		};
		setRows((prevState: any) =>
			prevState?.map((entry: any) => {
				if (entry?.id === row?.id) {
					return { ...entry, loadingBtn: true };
				}
				return entry;
			})
		);
		apiService.recoverUser(
			data,
			(response: AxiosResponse) => {
				setApiCall(true);
				setAlert({
					message: `User Recover Successfully`,
					severity: 'success',
				});
				setRows((prevState: any) =>
					prevState?.map((entry: any) => {
						if (entry?.id === row?.id) {
							return { ...entry, activeUser: true, loadingBtn: false };
						}
						return entry;
					})
				);
			},
			(error: AxiosError) => {
				setRows((prevState: any) =>
					prevState?.map((entry: any) => {
						if (entry?.id === row?.id) {
							return { ...entry, loadingBtn: false };
						}
						return entry;
					})
				);
			},
			row?.id
		);
	};

	const isTabletWidth = IsTabletWidth();
	const columns = [
		{
			field: 'firstname',
			headerName: 'Name',
			width: 250,
			renderCell: (params: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					{params?.row?.image ? (
						<Avatar
							alt="user-image"
							style={{
								maxWidth: '260px',
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
							}}
							src={params?.row?.image}
							sx={{
								marginRight: 1,
								width: '26px',
								height: '26px',
								img: {
									objectFit: 'contain',
								},
							}}
						/>
					) : (
						<Avatar
							sx={{
								marginRight: 1,
								width: '26px',
								height: '26px',
								img: {
									objectFit: 'contain',
								},
							}}
						>
							{params?.value.charAt(0)}
						</Avatar>
					)}
					{truncateField(params?.row?.firstname, 26)}
				</div>
			),
		},
		{ field: 'email', headerName: 'Email Address', width: 200 },
		{ field: 'telephone', headerName: 'Phone Number', width: 160 },
		{ field: 'type', headerName: 'Type', width: 120 },
		{
			field: 'permissions',
			headerName: 'Permissions',
			align: 'center',
			renderCell: (params: any) => (
				<IconButton onClick={params?.row?.onClick} disabled={!params?.row?.activeUser}>
					<PermissionIcon id="permission" />
				</IconButton>
			),
			width: 110,
		},
		{
			field: 'activeUser',
			headerName: 'Active User',
			width: 110,
			align: 'center',
			renderCell: (params: any) => (
				<div>
					{params.row.activeUser ? (
						<Check style={{ color: 'green' }} />
					) : (
						<Clear style={{ color: 'red' }} />
					)}
				</div>
			),
		},
		{
			field: 'recover',
			headerName: '',
			width: 120,
			renderCell: (params: any) =>
				!params?.row?.activeUser ? (
					<CustomButton
						title={
							params?.row?.loadingBtn ? (
								<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
							) : (
								'Recover'
							)
						}
						handleClick={() => handleRecover(params?.row)}
						heightVariant="medium"
						color="primary"
						styles={{
							width: '100px',
							height: '36px',
							padding: '7px 12px',
						}}
						disabled={params?.row?.loadingBtn}
					/>
				) : null,
		},
		{
			field: 'online',
			headerName: '',
			width: 150,
			renderCell: (params: any) => (
				<div>{params.row.active ? <span style={{ color: 'green' }}>Online</span> : null}</div>
			),
		},
	];

	const [page, setPage] = useState(pageNumber);
	const [totalPages, setTotalPages] = useState(1);

	//Mobile Screen States
	const [windowDimensions, setWindowDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	const updateWindowDimensions = () => {
		setWindowDimensions({
			width: window.innerWidth,
			height: window.innerHeight,
		});
	};

	const onRowsSelectionHandler = (ids: any) => {
		setSelectedItemIds(ids);
	};
	const handleRowClick = (params: any, event: any) => {
		const id = event?.target?.id;
		if (!['permission']?.includes(id) && params?.row?.activeUser) {
			setRowData(params?.row);
			setOpenEditDrawer(true);
		}
	};
	const handlePageChange = (event: any, value: number) => {
		if (page !== value) {
			setPage(value);
			dispatch(setPageNumber(value));
			setLoadingData(true);
			setSelectedItemIds([]);
			const searchParams = new URLSearchParams();
			searchParams.set('page', value?.toString());
			searchParams.set('limit', '25');

			//update URL with new params values
			const newUrl = new URL(window.location.href);
			newUrl.search = searchParams.toString();
			window.history.replaceState({}, '', newUrl.toString());
		}
	};
	const handleClick = () => {
		setOpenDrawer(true);
	};
	const handleSearch = (value: any) => {
		setSearching(true);
		setSearchValue(value);
		setPage(1);
		dispatch(setPageNumber(1));
	};

	const navigateToLastPage = (totalPages: number) => {
		setPage(totalPages);
		dispatch(setPageNumber(totalPages));
		navigate(
			`/${getBaseRoute(
				currentUser?.type
			)}/${id}/settings/teams&permissions?page=${totalPages}&limit=25`
		);
	};

	const deletingYourOwnAccount = () => {
		if (selectedItemIds?.includes(currentUser?.phone)) {
			clearUserSession('userDelete', undefined, navigate, () => apiService.logoutCallV2());
		}
	};

	const handleConfirm = () => {
		setLoading(true);
		let inActiveUserIds: string[] = [];
		rows?.forEach((row: any) => {
			if (!row?.activeUser) {
				inActiveUserIds?.push(row?.id);
			}
		});
		const ids = selectedItemIds?.filter((id: string) => !inActiveUserIds.includes(id));
		if (ids?.length) {
			const deletePromises = ids.map((id) => {
				return new Promise((resolve, reject) => {
					apiService.deleteUser(
						(response: AxiosResponse) => {
							resolve(response);
						},
						(error: AxiosError) => {
							reject(error);
						},
						id
					);
				});
			});

			Promise.all(deletePromises)
				.then((responses) => {
					deletingYourOwnAccount();
					setLoading(false);
					setAlert({
						message: `${selectedItemIds?.length > 1 ? 'Users' : 'User'} Deleted Successfully`,
						severity: 'success',
					});
					setSelectedItemIds([]);
					setRows((prevState: any) =>
						prevState?.map((row: any) => {
							if (ids?.includes(row?.id)) {
								return { ...row, activeUser: false };
							}
							return row;
						})
					);
					setApiCall(true);
					setDeleteProductModal(false);
				})
				.catch((errors) => {
					setLoading(false);
					displayCustomError('Something went wrong!');
				});
		} else {
			setAlert({
				message: `Deletion Failed: User is inactive.`,
				severity: 'info',
			});
			setSelectedItemIds([]);
			setLoading(false);
		}
	};

	const viewDetails = (item: any, e?: any) => {
		if (e?.target?.tagName === 'INPUT' || ['permission']?.includes(e?.target?.id)) {
			return;
		}
		setRowData(item);
		setOpenEditDrawer(true);
	};

	const handleSelectAll = (products: any) => {
		const ids: string[] =
			selectedItemIds?.length === rows?.length ? [] : rows?.map((item: any) => item?.id);
		setRows((prevState: any) => {
			return prevState?.map((item: any) => {
				return {
					...item,
					isSelection: !(selectedItemIds?.length === rows?.length),
				};
			});
		});

		setSelectedItemIds(ids);
	};

	const toggleSelect = (orderId: string, e: any) => {
		if (isSelected(orderId)) {
			setSelectedItemIds(selectedItemIds.filter((id) => id !== orderId));
		} else {
			setSelectedItemIds([...selectedItemIds, orderId]);
		}
	};

	const handleCheckboxChange = (id: string, e: any) => {
		toggleSelect(id, e);
	};

	const isSelected = (id: string) => selectedItemIds?.includes(id);

	const loadMore = () => {
		setLoadingItems(true);
		//@ts-ignore
		setPage((prevState: number) => prevState + 1);
	};

	const getAllUsers = () => {
		const limit = 25;
		apiService.getUsers(
			(response: AxiosResponse) => {
				const data = response?.data?.data;
				const totalCount = data?.totalCount;
				const totalPages = Math.max(Math.ceil(totalCount / limit), 1);
				const users = data?.users?.map((user: any) => {
					if (user?.telephone === currentUser?.phone) {
						return {
							...user,
							id: user?.telephone,
							activeUser: !user?.is_deleted,
							onClick: () => {
								setView('permission');
								setRowData(user);
							},
							active: true,
						};
					}
					return {
						...user,
						id: user?.telephone,
						activeUser: !user?.is_deleted,
						onClick: () => {
							setView('permission');
							setRowData(user);
						},
					};
				});

				if (isMobileWidth) {
					if (page === 1) {
						setRows(users);
					} else {
						//@ts-ignore
						setRows((prevState: any) => [...prevState, ...users]);
					}

					if (operation === 'add') {
						setOperation('');
					}
					if (isApiCall) {
						setApiCall(false);
					}
					setTotalPages(totalPages);
					setLoadingData(false);
					setSearching(false);
					setLoadingItems(false);
					setTotalCount(totalCount);
					return;
				}
				dispatch(setAllUsers(users));
				setRows(users);
				setTotalPages(totalPages);
				setLoadingData(false);
				setSearching(false);
				if (operation === 'add') {
					navigateToLastPage(totalPages);
					setOperation('');
				}
				if (isApiCall) {
					setApiCall(false);
				}
			},
			(error: AxiosError) => {
				console.error(error);
				setLoadingData(false);
				setSearching(false);
				displayCustomError('Something went wrong!');
				setLoadingItems(false);
			},
			{
				page: page,
				limit: limit,
				search: searchValue,
			}
		);
	};

	useEffect(() => {
		//persisted data
		if (allUsers?.length) {
			setRows(allUsers);
			setLoadingData(false);
		}
	}, []);

	useEffect(() => {
		if (isAdmin) {
			getAllUsers();
		} else {
			setLoadingData(false);
		}
	}, [isApiCall, page, operation, isAdmin]);

	useEffect(() => {
		if (isAdmin) {
			//debounce
			if (timeoutHandler.current) {
				clearTimeout(timeoutHandler.current);
			}
			timeoutHandler.current = setTimeout(() => {
				try {
					getAllUsers();
				} catch (err) {}
			}, 500);
		}
	}, [searchValue, isAdmin]);

	useEffect(() => {
		return () => {
			dispatch(setPageNumber(1));
		};
	}, []);

	//For Mobile Screen
	useEffect(() => {
		window.addEventListener('resize', updateWindowDimensions);

		return () => {
			window.removeEventListener('resize', updateWindowDimensions);
		};
	}, []);

	useEffect(() => {
		updateWindowDimensions();
	}, []);

	const modals = () => {
		return (
			<>
				{openDrawer ? (
					<CustomDrawer
						open={openDrawer || true}
						setOpen={setOpenDrawer}
						style={{ width: windowDimensions?.width <= 800 ? '100%' : '613px' }}
						header={false}
					>
						<UserCreation
							setOperation={setOperation}
							setAlert={setAlert}
							setOpenDrawer={setOpenDrawer}
							displayCustomError={displayCustomError}
							windowDimensions={windowDimensions}
						/>
					</CustomDrawer>
				) : null}
				{openEditDrawer ? (
					<CustomDrawer
						open={openEditDrawer}
						setOpen={setOpenEditDrawer}
						style={{ width: windowDimensions?.width <= 800 ? '100%' : '613px' }}
						header={false}
					>
						<UserEditable
							setApiCall={setApiCall}
							setAlert={setAlert}
							rowData={rowData}
							setOpenDrawer={setOpenEditDrawer}
							displayCustomError={displayCustomError}
							windowDimensions={windowDimensions}
						/>
					</CustomDrawer>
				) : null}
				{deleteProductModal ? (
					<CustomModal
						open={deleteProductModal}
						setOpen={setDeleteProductModal}
						title="Remove Users"
						style={stylingDeleteModal}
						children={
							<ConfirmModal
								content={`Deleting ${selectedItemIds?.length} ${
									selectedItemIds?.length > 1 ? 'users' : 'user'
								} . Are you sure?`}
								loading={false}
								handleClose={() => setDeleteProductModal(false)}
								handleConfirm={handleConfirm}
							/>
						}
					/>
				) : null}
			</>
		);
	};

	if (loadingData) {
		return <Loading />;
	}

	if (isMobileWidth) {
		return (
			<>
				{isAdmin ? (
					<>
						{view === 'permission' ? (
							<Permission
								user={rowData}
								displayCustomError={displayCustomError}
								setAlert={setAlert}
								setView={setView}
							/>
						) : (
							<StyledMobileContainer>
								{modals()}
								{collapse ? (
									<Collapse
										in={collapse}
										style={{
											display: 'flex',
											gap: '15px',
											alignItems: 'center',
											flexShrink: 0,
											padding: '16px',
										}}
									>
										<SearchBar
											style={{
												width: '328px',
												height: '36px',
											}}
											value={searchValue}
											onChange={handleSearch}
											isLoading={false}
											autoFocus
										/>
									</Collapse>
								) : null}
								<div id="filter-container">
									<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
										{rows?.length ? (
											<CustomTextButton
												style={{
													fontSize: '13px',
													fontWeight: 700,
													color: '#FF6120',
													textDecoration: 'none',
													maxWidth: '75px',
													marginTop: '4px',
												}}
												title={
													selectedItemIds?.length === rows?.length ? 'Deselect All' : 'Select All'
												}
												onClick={() => handleSelectAll(rows)}
											/>
										) : null}
										<SelectedItemsTextContainer
											selectedItemIds={selectedItemIds}
											filteredItems={rows}
											items
										/>
									</div>
								</div>

								<Container>
									{rows?.map((item: any) => {
										return (
											<SettingsCard
												key={item?.id}
												item={item}
												avatarUrl={item?.image}
												isSelected={isSelected}
												handleCheckboxChange={handleCheckboxChange}
												viewDetails={viewDetails}
												view="team"
												isContainCheckbox
											/>
										);
									})}

									{rows?.length < totalCount ? (
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												padding: '16px',
												flexShrink: 0,
												marginBottom: '10px',
											}}
										>
											{!loadingItems ? (
												<CustomTextButton
													title="Load More"
													onClick={loadMore}
													disabled={loadingItems}
												/>
											) : (
												<CircularProgress
													size={16}
													style={{ color: 'var(--default-text-color)' }}
												/>
											)}
										</div>
									) : null}
								</Container>

								<StyledBottomBtnContainer>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											width: '100%',
										}}
									>
										{selectedItemIds?.length ? (
											<CustomButton
												title="Delete Item(s)"
												handleClick={() => setDeleteProductModal(true)}
												styles={{
													width: '320px',
													height: '40px',
													background: '#F26184',
													color: '#FFFFFF',
													transition: 'background 0.3s ease, color 0.3s ease',
												}}
											/>
										) : (
											<CustomButton
												title="Add New User"
												handleClick={handleClick}
												heightVariant="medium"
												color="primary"
												styles={{
													width: '328px',
													height: '40px',
													padding: '7px 12px',
													transition: 'background 0.3s ease, color 0.3s ease',
												}}
												disabled={loading}
											/>
										)}
									</div>
								</StyledBottomBtnContainer>
							</StyledMobileContainer>
						)}
					</>
				) : (
					<CustomPermission
						icon={<LockIcon />}
						title="We are sorry..."
						paragraph={`You do not have permission to access teams and permission settings. Contact 
				an administrator to get permission.`}
					/>
				)}
			</>
		);
	}

	return (
		<>
			{isAdmin ? (
				<>
					{view === 'permission' ? (
						<Permission
							user={rowData}
							displayCustomError={displayCustomError}
							setAlert={setAlert}
							setView={setView}
						/>
					) : (
						<>
							<StyledContainer>
								<div className="container1">
									<p>
										User settings enable you to add new users and manage existing user information
										and permissions.
									</p>
									{windowDimensions?.width > 750 ? (
										<CustomButton
											title="Add New User"
											handleClick={handleClick}
											heightVariant="medium"
											color="primary"
											styles={{
												width: '131px',
												height: '36px',
												padding: '7px 12px',
											}}
											disabled={loading}
										/>
									) : null}
								</div>
								<div className="container1">
									<SearchBar
										style={{
											width: '328px',
											height: '36px',
										}}
										value={searchValue}
										onChange={handleSearch}
										isLoading={false}
									/>
									{selectedItemIds?.length ? (
										<SelectedItemsContainer>
											<Typography>{selectedItemIds?.length} items selected</Typography>
											<IconButton
												onClick={() => {
													setDeleteProductModal(true);
												}}
											>
												<DeleteIcon />
											</IconButton>
										</SelectedItemsContainer>
									) : null}
								</div>

								<Box>
									<CustomDataGrid2
										rows={rows}
										columns={columns}
										onRowsSelectionHandler={onRowsSelectionHandler}
										handleRowClick={handleRowClick}
										style={{
											transition: 'height 0.3s ease',
											maxHeight: 'calc(100vh - 320px)',
											minHeight: 'calc(100vh - 320px)',
										}}
										filterType="server"
										loading={searching}
										type="server"
									/>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											width: '100%',
											padding: '10px',
										}}
									>
										<Pagination
											color="primary"
											count={totalPages}
											page={page}
											onChange={handlePageChange}
										/>
									</Box>
								</Box>

								{openDrawer ? (
									<CustomDrawer
										open={openDrawer || true}
										setOpen={setOpenDrawer}
										style={{ width: windowDimensions?.width <= 800 ? '100%' : '613px' }}
										header={false}
									>
										<UserCreation
											setOperation={setOperation}
											setAlert={setAlert}
											setOpenDrawer={setOpenDrawer}
											displayCustomError={displayCustomError}
											windowDimensions={windowDimensions}
										/>
									</CustomDrawer>
								) : null}
								{openEditDrawer ? (
									<CustomDrawer
										open={openEditDrawer}
										setOpen={setOpenEditDrawer}
										style={{ width: windowDimensions?.width <= 800 ? '100%' : '613px' }}
										header={false}
									>
										<UserEditable
											setApiCall={setApiCall}
											setAlert={setAlert}
											rowData={rowData}
											setOpenDrawer={setOpenEditDrawer}
											displayCustomError={displayCustomError}
											windowDimensions={windowDimensions}
										/>
									</CustomDrawer>
								) : null}
								{deleteProductModal ? (
									<CustomModal
										open={deleteProductModal}
										setOpen={setDeleteProductModal}
										title="Remove Users"
										style={stylingDeleteModal}
										children={
											<ConfirmModal
												content={`Deleting ${selectedItemIds?.length} ${
													selectedItemIds?.length > 1 ? 'users' : 'user'
												} . Are you sure?`}
												loading={false}
												handleClose={() => setDeleteProductModal(false)}
												handleConfirm={handleConfirm}
											/>
										}
									/>
								) : null}
							</StyledContainer>
							{windowDimensions?.width <= 750 ? (
								<StyledBottomBtnContainer>
									<CustomButton
										title="Add New User"
										handleClick={handleClick}
										heightVariant="medium"
										color="primary"
										styles={{
											width: '328px',
											height: '36px',
											padding: '7px 12px',
										}}
										disabled={loading}
									/>
								</StyledBottomBtnContainer>
							) : null}
						</>
					)}
				</>
			) : (
				<CustomPermission
					icon={<LockIcon />}
					title="We are sorry..."
					paragraph={`You do not have permission to access teams and permission settings. Contact 
				an administrator to get permission.`}
				/>
			)}
		</>
	);
};

export default TeamsAndPermission;
